// src/components/themes/default/admin/BranchFraudTab.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import {
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Phone,
  Search,
  RefreshCw,
  Loader2,
  Sparkles,
  PhoneCall,
  User,
  MapPin,
  Check,
  X,
} from "lucide-react";

interface BranchFraudTabProps {
  branchInfo: any;
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
}

// 🇧🇩 বাংলাদেশি অপারেটর ও ফ্রড চেকার হেল্পার
const BD_OPERATORS: Record<string, string> = {
  "013": "Grameenphone",
  "017": "Grameenphone",
  "014": "Banglalink",
  "019": "Banglalink",
  "018": "Robi",
  "016": "Airtel",
  "015": "Teletalk",
};

export default function BranchFraudTab({
  branchInfo,
  subdomain,
  theme = "light",
  primaryColor = "#059669",
}: BranchFraudTabProps) {
  const isLight = theme === "light";
  const branchId = branchInfo?.id;
  const branchName = branchInfo?.name || "Branch Outlet";

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [fraudMap, setFraudMap] = useState<Record<string, any>>({});

  // 🌟 Live BD Mobile Number & Risk Checker State
  const [testPhone, setTestPhone] = useState("");
  const [testAddress, setTestAddress] = useState("");
  const [analyzingPhone, setAnalyzingPhone] = useState(false);
  const [checkerResult, setCheckerResult] = useState<any | null>(null);

  const getBranchToken = useCallback(() => {
    if (typeof window === "undefined") return "";
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("branch_auth_")) {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data?.token) return data.token;
        }
      }
    } catch {}
    return localStorage.getItem("token") || "";
  }, []);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  // 🌟 অর্ডার ও ফ্রড স্কোর ফেচ এবং সিনক্রোনাইজেশন
  const fetchAllBranchOrders = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const token = getBranchToken();
      const headers: Record<string, string> = {
        Accept: "application/json",
        "X-Branch-ID": String(branchId),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // ১. ব্রাঞ্চের আসল অর্ডারগুলো ফেচ করা
      const ordersRes = await fetch(
        getApiUrl(`/api/v1/orders/?branch_id=${branchId}&admin=true`),
        { headers }
      );

      let fetchedOrders: any[] = [];
      if (ordersRes.ok) {
        const oData = await ordersRes.json();
        fetchedOrders = Array.isArray(oData) ? oData : oData.results || [];
      }

      // ২. ফ্রড ডিটেকশন ডাটা ফেচ করা
      const fraudRes = await fetch(
        getApiUrl(`/api/v1/ai/fraud-detections/?branch_id=${branchId}`),
        { headers }
      );

      const fMap: Record<string, any> = {};
      if (fraudRes.ok) {
        const fData = await fraudRes.json();
        const fList = Array.isArray(fData) ? fData : fData.results || [];
        fList.forEach((item: any) => {
          if (item.order) fMap[String(item.order)] = item;
          if (item.order_number) fMap[String(item.order_number)] = item;
        });
      }

      setOrders(fetchedOrders);
      setFraudMap(fMap);
    } catch (err) {
      console.error("Error fetching branch fraud data:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiUrl, getBranchToken]);

  useEffect(() => {
    fetchAllBranchOrders();
  }, [fetchAllBranchOrders]);

  // 🌟 অর্ডার রিস্ক স্কোর ক্যালকুলেটর
  const calculateOrderRisk = (order: any) => {
    const fraudRecord =
      fraudMap[String(order.id)] || fraudMap[String(order.order_number)];

    if (fraudRecord) {
      return {
        score: Number(fraudRecord.risk_score || 0),
        level: fraudRecord.risk_level || "low",
        factors: fraudRecord.factors || [],
      };
    }

    // Heuristic Fallback
    const total = parseFloat(order.total || 0);
    const method = String(order.payment_method || "cod").toLowerCase();
    const phone =
      order.customer?.phone ||
      order.shipping_address?.phone ||
      order.shipping_address?.phoneNumber ||
      "";

    let score = 15.0;
    const isWalkIn = !phone || String(order.order_number || "").includes("POS");

    if (isWalkIn) {
      score = 36.2; // Walk-in POS Orders standard score
    } else {
      const cleanPhone = phone.replace(/[^0-9]/g, "");
      const prefix = cleanPhone.startsWith("880")
        ? cleanPhone.slice(3, 6)
        : cleanPhone.slice(0, 3);

      if (!BD_OPERATORS[prefix] || cleanPhone.length !== 11) {
        score += 40.0;
      }
      if (total > 8000 && method === "cod") {
        score += 25.0;
      }
    }

    const level = score >= 65 ? "high" : score >= 30 ? "medium" : "low";
    return { score: Number(score.toFixed(1)), level, factors: [] };
  };

  // 🌟 Live BD Mobile Number & Risk Checker অ্যাকশন
  const handleAnalyzePhone = (e: React.FormEvent) => {
    e.preventDefault();
    if (!testPhone.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Phone Required",
        text: "একটি বাংলাদেশি মোবাইল নম্বর লিখুন।",
        confirmButtonColor: primaryColor,
      });
      return;
    }

    setAnalyzingPhone(true);
    setTimeout(() => {
      const raw = testPhone.trim().replace(/[^0-9]/g, "");
      const cleaned = raw.startsWith("880") ? raw.slice(2) : raw;
      const prefix = cleaned.slice(0, 3);
      const operator = BD_OPERATORS[prefix] || "Unknown Operator";

      let score = 12.0;
      const flags: string[] = [];

      if (cleaned.length !== 11 || !BD_OPERATORS[prefix]) {
        score += 48.0;
        flags.push("ভুল মোবাইল নম্বর ফরম্যাট অথবা অপরেটর সনাক্ত করা যায়নি");
      } else {
        flags.push(`বৈধ মোবাইল নম্বর (${operator} নেটওয়ার্ক)`);
      }

      if (/(\d)\1{5,}/.test(cleaned)) {
        score += 35.0;
        flags.push("নম্বরে সন্দেহজনক ডামি বা পুনরাবৃত্ত ডিজিট রয়েছে");
      }

      if (testAddress && testAddress.trim().length < 8) {
        score += 20.0;
        flags.push("ডেলিভারি ঠিকানা অসম্পূর্ণ বা খুব ছোট");
      } else if (testAddress) {
        flags.push("ঠিকানা যাচাই সম্পন্ন");
      }

      const finalScore = Math.min(100, score);
      const level = finalScore >= 65 ? "high" : finalScore >= 30 ? "medium" : "low";

      setCheckerResult({
        phone: cleaned,
        operator,
        score: finalScore,
        level,
        flags,
        recommendation:
          level === "high"
            ? "উচ্চ ঝুঁকি: পার্সেল পাঠানোর পূর্বে অগ্রিম ডেলিভারি চার্জ আদায় করুন।"
            : level === "medium"
            ? "মাঝারি ঝুঁকি: কাস্টমারকে কল দিয়ে ঠিকানা নিশ্চিত করে ডেলিভারি দিন।"
            : "নিরাপদ: অবিলম্বে পার্সেল প্যাকিং ও কুরিয়ারে হস্তান্তর করা যাবে।",
      });

      setAnalyzingPhone(false);
    }, 400);
  };

  // কেপিআই হিসাব
  const evaluatedStats = useMemo(() => {
    let high = 0;
    let medium = 0;
    let safe = 0;

    orders.forEach((o) => {
      const { level } = calculateOrderRisk(o);
      if (level === "high") high++;
      else if (level === "medium") medium++;
      else safe++;
    });

    return { high, medium, safe, total: orders.length };
  }, [orders, fraudMap]);

  return (
    <div className="w-full space-y-6 font-sans animate-in fade-in pb-12">
      {/* 🌟 ১. টপ হেডার বার */}
      <div
        className={`p-6 rounded-3xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
          isLight
            ? "bg-white border-slate-200 shadow-xs"
            : "bg-slate-900 border-slate-800 text-white"
        }`}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-rose-50 text-rose-500 border border-rose-200">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
              AI Fraud Shield & COD Risk Analyzer
            </h1>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed pl-10">
            Cash on Delivery (COD) fake orders, dummy mobile numbers, and Return-to-Origin (RTO) prevention
          </p>
        </div>

        <button
          onClick={fetchAllBranchOrders}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold border transition-all cursor-pointer self-start sm:self-auto ${
            isLight
              ? "bg-white hover:bg-slate-50 border-slate-200 text-slate-700 shadow-2xs"
              : "bg-slate-950 hover:bg-slate-800 border-slate-800 text-slate-200"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* 🌟 ২. তিনটি রঙিন KPI কার্ড (১ম স্ক্রিনশটের মতো হুবহু) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* কার্ড ১: HIGH RISK ORDERS */}
        <div className="p-6 rounded-3xl bg-[#fff5f5] border border-[#fed7d7] text-slate-900 space-y-2 shadow-2xs">
          <div className="flex items-center gap-2 text-rose-600">
            <span className="w-2 h-2 rounded-full bg-rose-500" />
            <h4 className="text-[11px] font-black uppercase tracking-wider">
              HIGH RISK ORDERS
            </h4>
          </div>
          <div className="text-4xl font-black font-mono text-slate-900">
            {evaluatedStats.high}
          </div>
          <p className="text-xs text-slate-500 leading-snug">
            Risky to dispatch without collecting advance delivery charge
          </p>
        </div>

        {/* কার্ড ২: MEDIUM RISK */}
        <div className="p-6 rounded-3xl bg-[#fffaf0] border border-[#feebc8] text-slate-900 space-y-2 shadow-2xs">
          <div className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-3.5 h-3.5" />
            <h4 className="text-[11px] font-black uppercase tracking-wider">
              MEDIUM RISK
            </h4>
          </div>
          <div className="text-4xl font-black font-mono text-slate-900">
            {evaluatedStats.medium}
          </div>
          <p className="text-xs text-slate-500 leading-snug">
            Call customer to verify address details before parcel handover
          </p>
        </div>

        {/* কার্ড ৩: VERIFIED SAFE ORDERS */}
        <div className="p-6 rounded-3xl bg-[#f0fff4] border border-[#c6f6d5] text-slate-900 space-y-2 shadow-2xs">
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <h4 className="text-[11px] font-black uppercase tracking-wider">
              VERIFIED SAFE ORDERS
            </h4>
          </div>
          <div className="text-4xl font-black font-mono text-slate-900">
            {evaluatedStats.safe}
          </div>
          <p className="text-xs text-slate-500 leading-snug">
            Verified safe for immediate packaging and courier dispatch
          </p>
        </div>
      </div>

      {/* 🌟 ৩. Live BD Mobile Number & Risk Checker টুলবক্স */}
      <div
        className={`p-6 rounded-3xl border space-y-4 shadow-xs ${
          isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
        }`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-black text-slate-900 dark:text-white">
            Live BD Mobile Number & Risk Checker
          </h3>
        </div>

        <form
          onSubmit={handleAnalyzePhone}
          className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
        >
          <div className="md:col-span-5">
            <input
              type="text"
              placeholder="Customer Phone (e.g. 01700000000)"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-2xl text-xs font-mono font-medium outline-none border transition-all ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white focus:border-emerald-500"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            />
          </div>

          <div className="md:col-span-5">
            <input
              type="text"
              placeholder="Delivery Address (Optional)"
              value={testAddress}
              onChange={(e) => setTestAddress(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-2xl text-xs font-medium outline-none border transition-all ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-900 focus:bg-white focus:border-emerald-500"
                  : "bg-slate-950 border-slate-800 text-white"
              }`}
            />
          </div>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={analyzingPhone}
              style={{ backgroundColor: primaryColor }}
              className="w-full py-2.5 rounded-2xl text-white text-xs font-black flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/20 hover:opacity-90 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
            >
              {analyzingPhone ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5" />
              )}
              <span>Analyze Risk</span>
            </button>
          </div>
        </form>

        {/* Checker Result Display */}
        {checkerResult && (
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs animate-fadeIn">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-slate-900">
                  {checkerResult.phone}
                </span>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-100 text-blue-800">
                  {checkerResult.operator}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${
                    checkerResult.level === "high"
                      ? "bg-rose-100 text-rose-800"
                      : checkerResult.level === "medium"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {checkerResult.level} Risk ({checkerResult.score}/100)
                </span>
              </div>
              <p className="text-slate-600 font-medium">
                {checkerResult.recommendation}
              </p>
            </div>
            <button
              onClick={() => setCheckerResult(null)}
              className="text-slate-400 hover:text-slate-600 text-xs font-bold"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* 🌟 ৪. Orders & Risk Evaluation List টেবিল (১ম স্ক্রিনশটের হুবহু কলাম) */}
      <div
        className={`p-6 rounded-3xl border space-y-4 shadow-xs ${
          isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
        }`}
      >
        <div className="flex items-center justify-between border-b pb-4 border-slate-100 dark:border-slate-800">
          <h3 className="text-sm font-black text-slate-900 dark:text-white">
            Orders & Risk Evaluation List (Total: {orders.length})
          </h3>
          <span className="text-xs font-mono font-bold text-slate-400">
            Outlet: {branchName}
          </span>
        </div>

        {loading ? (
          <div className="py-20 text-center space-y-2">
            <Loader2 style={{ color: primaryColor }} className="w-8 h-8 animate-spin mx-auto" />
            <p className="text-xs font-bold text-slate-400">Evaluating branch orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-slate-400 space-y-2">
            <CheckCircle2 className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-xs font-bold">এখনো কোনো অর্ডার পাওয়া যায়নি।</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-xs min-w-[700px] border-collapse">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="py-3 px-4">ORDER ID</th>
                  <th className="py-3 px-4">CUSTOMER & PHONE</th>
                  <th className="py-3 px-4">BILL & METHOD</th>
                  <th className="py-3 px-4">AI FRAUD STATUS</th>
                  <th className="py-3 px-4 text-right">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
                {orders.map((ord: any) => {
                  const risk = calculateOrderRisk(ord);
                  const isHigh = risk.level === "high";
                  const isMed = risk.level === "medium";

                  const customerName =
                    ord.customer?.name ||
                    ord.customer?.first_name ||
                    ord.shipping_address?.name ||
                    (String(ord.order_number || "").includes("POS")
                      ? "Counter Customer (Walk-in)"
                      : "Online Customer");

                  const phone =
                    ord.customer?.phone ||
                    ord.shipping_address?.phone ||
                    ord.shipping_address?.phoneNumber ||
                    (String(ord.order_number || "").includes("POS")
                      ? "POS-Direct"
                      : "N/A");

                  const method = (ord.payment_method || "CASH").toUpperCase();
                  const total = parseFloat(ord.total || 0).toLocaleString();

                  return (
                    <tr
                      key={ord.id}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      {/* ORDER ID */}
                      <td className="py-4 px-4 font-mono font-black text-slate-900 dark:text-white">
                        {ord.order_number || `ORD-${ord.id}`}
                      </td>

                      {/* CUSTOMER & PHONE */}
                      <td className="py-4 px-4">
                        <div className="font-bold text-slate-900 dark:text-white">
                          {customerName}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400">
                          {phone}
                        </div>
                      </td>

                      {/* BILL & METHOD */}
                      <td className="py-4 px-4">
                        <div className="font-mono font-black text-slate-900 dark:text-white">
                          ৳{total}
                        </div>
                        <div className="text-[9px] font-mono font-bold text-amber-600">
                          {method === "COD" ? "CASH ON DELIVERY" : `${method} (COUNTER)`}
                        </div>
                      </td>

                      {/* AI FRAUD STATUS */}
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${
                            isHigh
                              ? "bg-rose-50 text-rose-600 border border-rose-200"
                              : isMed
                              ? "bg-amber-50 text-amber-600 border border-amber-200"
                              : "bg-emerald-50 text-emerald-600 border border-emerald-200"
                          }`}
                        >
                          {isHigh ? (
                            <ShieldAlert className="w-3.5 h-3.5" />
                          ) : isMed ? (
                            <AlertTriangle className="w-3.5 h-3.5" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          )}
                          <span>
                            {isHigh
                              ? `High Risk (${risk.score}/100)`
                              : isMed
                              ? `Medium Risk (${risk.score}/100)`
                              : `Verified Safe (${risk.score}/100)`}
                          </span>
                        </span>
                      </td>

                      {/* ACTION (Call Button) */}
                      <td className="py-4 px-4 text-right">
                        {phone && phone !== "POS-Direct" && phone !== "N/A" ? (
                          <a
                            href={`tel:${phone}`}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-bold transition-all cursor-pointer"
                          >
                            <Phone className="w-3 h-3" />
                            <span>Call</span>
                          </a>
                        ) : (
                          <span className="text-[11px] font-bold text-slate-400">
                            Walk-in
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}